import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Dropdown, makeStyles, Option } from "@fluentui/react-components";
import { useIntl } from "react-intl";
import { Video20Filled } from "@fluentui/react-icons";

const useStyles = makeStyles({
  label: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }
});

export function PreferredVideoSelect({ isVideoEnabled, videoOptions, onVideoChange, fetchVideoDevices }) {
  const intl = useIntl();
  const styles = useStyles();
  const preferredVideoValue = videoOptions.value;
  const preferredVideoLabel = videoOptions.options?.find(option => option.value === preferredVideoValue)?.label;
  const videoOptionsOptions = videoOptions.options;

  /**
   * Handler for when a new video option is selected.
   * Ensures that the selected value is different before triggering the callback.
   */
  const handleOptionSelect = useCallback(
    (_, data) => {
      if (data.optionValue !== preferredVideoValue) {
        onVideoChange(data.optionValue);
      }
    },
    [onVideoChange, preferredVideoValue]
  );

  const handleClick = useCallback(() => {
    fetchVideoDevices();
  }, [fetchVideoDevices]);

  return (
    <Dropdown
      placeholder={intl.formatMessage({ id: "preferredVideoSelect.placeholder", defaultMessage: "Select Video" })}
      onOptionSelect={handleOptionSelect}
      appearance="filled-darker"
      disabled={!isVideoEnabled}
      button={
        <span className={styles.label}>
          <Video20Filled />
          &nbsp;
          {preferredVideoLabel}
        </span>
      }
      value={preferredVideoLabel}
      selectedOptions={[preferredVideoValue]}
      onClick={handleClick}
    >
      {videoOptionsOptions &&
        videoOptionsOptions.map(option => (
          <Option key={option.value} value={option.value} text={option.label}>
            <span className={styles.label}>{option.label}</span>
          </Option>
        ))}
    </Dropdown>
  );
}

PreferredVideoSelect.propTypes = {
  isVideoEnabled: PropTypes.bool.isRequired,
  videoOptions: PropTypes.shape({
    value: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired,
  onVideoChange: PropTypes.func.isRequired
};
